/**
 * Local Delivery Cart Domain helpers.
 * Pure logic extracted from local-delivery-cart orchestrator.
 */
(function () {
  'use strict';

  function normalizeShopifyNumericId(value) {
    var raw = String(value == null ? '' : value).trim();
    if (!raw) return '';
    var gidMatch = raw.match(/\/(\d+)$/);
    if (gidMatch && gidMatch[1]) return gidMatch[1];
    var numericMatch = raw.match(/(\d+)/);
    if (numericMatch && numericMatch[1]) return numericMatch[1];
    return raw;
  }

  function uniqueCartProductIds(cart) {
    var items = (cart && cart.items) || [];
    var set = {};
    items.forEach(function (item) {
      var normalized = normalizeShopifyNumericId(item && item.product_id);
      if (normalized) set[normalized] = true;
    });
    return Object.keys(set);
  }

  function detectOccasion(cart, occasions) {
    if (!cart || !cart.items || !occasions || occasions.length === 0) return null;
    var productSet = {};
    (cart.items || []).forEach(function (item) {
      var normalized = normalizeShopifyNumericId(item && item.product_id);
      if (normalized) productSet[normalized] = true;
    });
    for (var i = 0; i < occasions.length; i++) {
      var occ = occasions[i];
      if (!occ || occ.active === false || !occ.product_ids || occ.product_ids.length === 0) continue;
      var hasAny = occ.product_ids.some(function (pid) {
        var normalized = normalizeShopifyNumericId(pid);
        return normalized && !!productSet[normalized];
      });
      if (hasAny) return occ;
    }
    return null;
  }

  function buildOccasionBlackouts(occasions) {
    var entries = [];
    if (!occasions) return entries;
    occasions.forEach(function (occ) {
      var cur = occ.start_date;
      while (cur <= occ.end_date) {
        entries.push({
          date: cur,
          reason: 'Reserved for ' + occ.name + ' orders',
          url: occ.collection_url || '',
        });
        var parts = cur.split('-');
        var d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
        d.setDate(d.getDate() + 1);
        cur =
          d.getFullYear() +
          '-' +
          String(d.getMonth() + 1).padStart(2, '0') +
          '-' +
          String(d.getDate()).padStart(2, '0');
      }
    });
    return entries;
  }

  function getNonOccasionItems(cart, matchedOccasion) {
    if (!cart || !cart.items || !matchedOccasion || !matchedOccasion.product_ids) return [];
    var pidSet = {};
    (matchedOccasion.product_ids || []).forEach(function (id) {
      var normalized = normalizeShopifyNumericId(id);
      if (normalized) pidSet[normalized] = true;
    });
    var seen = {};
    return (cart.items || []).filter(function (item) {
      var normalizedProductId = normalizeShopifyNumericId(item && item.product_id);
      if (normalizedProductId && pidSet[normalizedProductId]) return false;
      if (normalizedProductId && seen[normalizedProductId]) return false;
      if (normalizedProductId) seen[normalizedProductId] = true;
      return true;
    });
  }

  function isMixedCart(cart, matchedOccasion) {
    return !!(matchedOccasion && getNonOccasionItems(cart, matchedOccasion).length > 0);
  }

  function currentAdvanceMethodKey(isCollection, activeTimeslotType) {
    if (isCollection) return 'collection';
    if (activeTimeslotType === 'express') return 'express';
    return 'delivery';
  }

  function intersectsAnyProduct(productIds, targetIds) {
    if (!productIds || !targetIds || productIds.length === 0 || targetIds.length === 0) return false;
    var set = {};
    productIds.forEach(function (id) {
      if (!id) return;
      set[String(id)] = true;
    });
    for (var i = 0; i < targetIds.length; i++) {
      if (set[String(targetIds[i])]) return true;
    }
    return false;
  }

  function intersectsAnyVariant(variantIds, targetIds) {
    if (!variantIds || !targetIds || variantIds.length === 0 || targetIds.length === 0) return false;
    var set = {};
    variantIds.forEach(function (id) {
      var normalized = normalizeShopifyNumericId(id);
      if (!normalized) return;
      set[String(normalized)] = true;
    });
    for (var i = 0; i < targetIds.length; i++) {
      var normalizedTarget = normalizeShopifyNumericId(targetIds[i]);
      if (normalizedTarget && set[String(normalizedTarget)]) return true;
    }
    return false;
  }

  function computeActiveAdvanceLeadDays(opts) {
    opts = opts || {};
    if (opts.matchedOccasion) return 0;
    var methodKey = opts.methodKey || 'delivery';
    var rules = Array.isArray(opts.rules) ? opts.rules : [];
    var cartProductIds = Array.isArray(opts.cartProductIds) ? opts.cartProductIds : [];
    var cartVariantIds = Array.isArray(opts.cartVariantIds) ? opts.cartVariantIds : [];
    var maxLead = 0;
    rules.forEach(function (rule) {
      if (!rule) return;
      if (!rule.methods || rule.methods[methodKey] !== true) return;
      if (
        !intersectsAnyProduct(cartProductIds, rule.productIds || []) &&
        !intersectsAnyVariant(cartVariantIds, rule.variantIds || [])
      ) return;
      var lead = Math.max(0, Math.floor(Number(rule.leadDays) || 0));
      if (lead > maxLead) maxLead = lead;
    });
    return maxLead;
  }

  function getDateBounds(opts) {
    opts = opts || {};
    var matchedOccasion = opts.matchedOccasion || null;
    var DATE = opts.DATE;
    var config = opts.config || {};
    var activeAdvanceLeadDays = opts.activeAdvanceLeadDays || 0;
    if (matchedOccasion) {
      return { min: matchedOccasion.start_date, max: matchedOccasion.end_date };
    }
    return {
      min: DATE.addDaysStrSgt(activeAdvanceLeadDays || 0),
      max: DATE.maxDateStrSgt(config.booking_window || 120),
    };
  }

  function isDateWithinBounds(dateIso, bounds) {
    if (!dateIso || !/^\d{4}-\d{2}-\d{2}$/.test(dateIso)) return false;
    if (!bounds || !bounds.min || !bounds.max) return false;
    return dateIso >= bounds.min && dateIso <= bounds.max;
  }

  function isDateAllowed(dateIso, bounds, isBlackoutDateFn) {
    if (!isDateWithinBounds(dateIso, bounds)) return false;
    return !(isBlackoutDateFn && isBlackoutDateFn(dateIso));
  }

  window.WF_LD_CART_DOMAIN = {
    normalizeShopifyNumericId: normalizeShopifyNumericId,
    uniqueCartProductIds: uniqueCartProductIds,
    detectOccasion: detectOccasion,
    buildOccasionBlackouts: buildOccasionBlackouts,
    getNonOccasionItems: getNonOccasionItems,
    isMixedCart: isMixedCart,
    currentAdvanceMethodKey: currentAdvanceMethodKey,
    intersectsAnyProduct: intersectsAnyProduct,
    intersectsAnyVariant: intersectsAnyVariant,
    computeActiveAdvanceLeadDays: computeActiveAdvanceLeadDays,
    getDateBounds: getDateBounds,
    isDateWithinBounds: isDateWithinBounds,
    isDateAllowed: isDateAllowed,
  };
})();
